// src/modules/Courier/pages/CourierModal/CourierModal.jsx
import React, { createContext, useContext, useEffect, useState, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import styles from './CourierModal.module.css';

// Импорт всех страниц внутри модалки
import DispatchPage    from './pages/DispatchPage.jsx';
import ScannersPage    from './pages/ScannersPage.jsx';
import IssueCheckPage  from './pages/IssueCheckPage.jsx';
import CheckPage       from './pages/CheckPage.jsx';
import ReceiptPage     from './pages/ReceiptPage.jsx';
import FailurePage     from './pages/FailurePage.jsx';
import ReceiptMapPage from './pages/ReceiptMapPage.jsx';
import RouteOrderPage from './pages/RouteOrderPage.jsx';
import ContactsPage from './pages/ContactsPage.jsx';
import ScannerSettingsPage from './pages/ScannerSettingsPage.jsx';

const ModalPageContext = createContext({ goTo: () => {}, close: () => {} });

export default function CourierModal({ isOpen, onClose, initialPage, receiptId = null, routeId = null }) {
  const [pageKey, setPageKey] = useState(initialPage);
  const [showScannerPage, setShowScannerPage] = useState(false);

  // При открытии сбрасываем страницу
  useEffect(() => { if (isOpen) setPageKey(initialPage); }, [isOpen, initialPage]);

  // Контроль ленивого монтирования ScannersPage
  useEffect(() => {
    if (isOpen && pageKey === 'scan') setShowScannerPage(true);
    else setShowScannerPage(false);
  }, [isOpen, pageKey]);

  // Закрытие по Escape
  useEffect(() => {
    if (!isOpen) return;
    const onEsc = e => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [isOpen, onClose]);

    const handleCloseIssueCheck = () => {
    // 1. Первое подтверждение выхода с незавершённой страницы чеков
    const confirmExit = window.confirm(
      'Вы действительно хотите покинуть страницу формирования чека?\n' +
      'Чек не будет создан и отправлен, а доставка не будет считаться завершенной.'
    );
    if (!confirmExit) {
      // Если нажали “Отмена” — остаёмся на странице
      return;
    }

    // 2. Второе напоминание об обязательности формирования чека
    const confirmMandatory = window.confirm(
      'Формирование чека является обязательным шагом операции.\n' +
      'Пропустить его можно только при недоступности онлайн-кассы.\n' +
      'Продолжить и выйти без чека?'
    );
    if (!confirmMandatory) {
      // Если снова “Отмена” — не закрываем модалку
      return;
    }

    // Если оба раза нажали “ОК” — закрываем модалку
    onClose();
  };

  if (!isOpen) return null;

  // Маппинг страниц внутри модалки
  const renderPage = () => {
    switch (pageKey) {
      case 'dispatch':
        return <DispatchPage routeId={routeId} />;

      case 'scan':
        return showScannerPage ? (
          <Suspense fallback={<div>Загрузка сканера...</div>}>
            <ScannersPage
              onClose={() => { setShowScannerPage(false); onClose(); }}
              onFailure={() => setPageKey('failure')}
            />
          </Suspense>
        ) : null;

      case 'scannerSettings':
        return <ScannerSettingsPage receiptId={receiptId} />;

      case 'issueCheck':
        return <IssueCheckPage receiptId={receiptId} />;

      case 'check':
        return <CheckPage receiptId={receiptId} />;

      case 'receipt':
        return <ReceiptPage receiptId={receiptId} />;

      case 'contacts':
        return <ContactsPage receiptId={receiptId} />;

      case 'failure':
        return <FailurePage receiptId={receiptId} />;

      case 'map':
        return <ReceiptMapPage receiptId={receiptId} />;

      case 'order':
      return <RouteOrderPage  routeId={routeId} />;

      default:
        console.error(`Неизвестная страница модалки: ${pageKey}`);
        return null;
    }
  };

  return (
    <div className={styles.overlay} onClick={pageKey === 'issueCheck' ? handleCloseIssueCheck : onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          {pageKey === 'scan' ? 'Сканирование' :
           pageKey === 'scannerSettings' ? 'Настройки сканера' :
           pageKey === 'dispatch' ? 'Отправка' :
           pageKey === 'issueCheck' ? 'Формирование чека' :
           pageKey === 'check' ? 'Проверка' :
           pageKey === 'receipt' ? 'Квитанция' :
           pageKey === 'map'? 'Карта':
           pageKey === 'failure' ? 'Укажиите причину' : null}
        </div>
        <div className={styles.body}>
          <ModalPageContext.Provider value={{ goTo: setPageKey, close: onClose }}>
            {renderPage()}
          </ModalPageContext.Provider>
        </div>
        <div className={styles.footer}>
          <button
            className={styles.closeButton}
            onClick={pageKey === 'issueCheck' ? handleCloseIssueCheck : onClose}
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
}

CourierModal.propTypes = {
  isOpen:      PropTypes.bool.isRequired,
  onClose:     PropTypes.func.isRequired,
  initialPage: PropTypes.string.isRequired,
  receiptId:   PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  routeId:     PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export function useModalPage() {
  return useContext(ModalPageContext);
}